/**
 * The one file that knows `fflate` exists. Master Phase 9.
 *
 * `fflate` (~8 KB, zero dependencies) was chosen over `jszip` (~95 KB+) for
 * the same bundle-discipline reason every prior phase has applied — nothing
 * here needs more than "zip a few files, unzip a few files," and the size
 * difference is real weight for a Settings-only feature. Isolated to this
 * file so a future swap touches one place, not every call site.
 *
 * The async API (`zip`/`unzip`), not the sync one (`zipSync`/`unzipSync`), is
 * used deliberately — a video blob can be tens of megabytes, and the sync
 * API blocks the main thread for the whole operation.
 */

export async function zipEntries(entries: Record<string, Uint8Array>): Promise<Uint8Array> {
  const { zip } = await import('fflate');
  return await new Promise<Uint8Array>((resolve, reject) => {
    zip(entries, { level: 6 }, (error, data) => {
      if (error) reject(error);
      else resolve(data);
    });
  });
}

export async function unzipEntries(bytes: Uint8Array): Promise<Record<string, Uint8Array>> {
  const { unzip } = await import('fflate');
  return await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
    unzip(bytes, (error, data) => {
      if (error) reject(error);
      else resolve(data);
    });
  });
}

/**
 * A real, non-shared `ArrayBuffer` slice — what `BlobPart` requires. A plain
 * `Uint8Array`'s `.buffer` is typed `ArrayBufferLike` (it could back onto a
 * `SharedArrayBuffer`), which current DOM types no longer accept directly.
 */
export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
